<template>
  <div class="heatmap-report">
    <div class="fixed_content">
      <h2 hidden>{{ $t('reporting.title') }}</h2>
      <heatmap
        mode="default"
        :aid="aid"
        :infoData="infoData"
        :activeSelector="activeSelector"
        :userActionData="userActionData"
        :conversion="conversion"
        :activityStatistics="activityStatistics"
        :initialEventType="initialEventType"
        :isDownloading="isDownloading"
        :heatmapType="heatmapType"
        :hourData="hourData"
        :freqPv="freqPv"
        :freqTotalPv="freqTotalPv"
        :isFrequency="isFrequency"
        :freqData="freqData"
        :freq-option="freqOption"
        @setHeatmapType="setHeatmapType"
        @onDownloading="onDownloading"
        @onHeatmapLoadStart="onHeatmapLoadStart"
        @onHeatmapLoadEnd="onHeatmapLoadEnd"
        @onHeatmapData="onHeatmapData"
        @onInfoData="onInfoData"
        @onTimeSeries="onTimeSeries"
        @onRefererChange="referer = $event"
        @onMetricChange="metric = $event"
        @onActiveSelectorChange="activeSelector = $event"
        @onUserActionDataChange="userActionData = $event"
        @showFrequency="showFrequency"
        @apiParams="apiParams"
        @onSegmentsData="onSegmentsData"
        @changeDevice="device = $event"
      />
    </div>
    <scroll-content class="scroll_content" ref="scroll_content">
      <div class="reporting-heatmap-analysis">

        <component :is="reportComponent" ref="reportComponent"
          :isLoading="isLoading"
          :isAMP="applyData.bAMP"
          :infoData="infoData"
          :metric="metric"
          :referer="referer"
          :reportName="reportName"
          :reportData="reportData"
          :activeSelector="activeSelector"
          :userActionData="userActionData"
          :heatmapType="heatmapType"
          :hourData="hourData"
          :freqPv="freqPv"
          :freqTotalPv="freqTotalPv"
          :freq-option="freqOption"
          :freqData="freqData"
          :isFrequency="isFrequency"
          :is-timeseries="reportPeriod.start === reportPeriod.end"
          :apiParamsData="apiParamsData"
          :segmentsDataForCapture="segmentsDataForCapture"
          @setHeatmapType="setHeatmapType"
          @onDownloading="onDownloading"
          @onReportLoading="onReportLoading"
          @onConversionChange = "conversion = $event"
          @onActivityStatisticsChange = "activityStatistics = $event"
          @onActiveSelectorChange="activeSelector = $event"
          @onUserActionDataChange="userActionData = $event">
          <h3 class="fan-o-meter" v-if="isFrequency || reportPeriod.start === reportPeriod.end">
            Fan-o-Meter
          </h3>
          <ReportTimeSeries
            v-if="reportPeriod.start === reportPeriod.end"
            :reportData="reportData"
            :infoData="infoData"
            :timeSeries="timeSeries"
            :is-frequency="isFrequency"
            @onDeviceDataByHour="onDeviceDataByHour"
          />
          <ReportFrequency
            v-if="isPlusPlan && isFrequency"
            :apiParamsData="apiParamsData"
            @getFreqTotalPv="getFreqTotalPv"
            @onFreqData="onFreqData"
            @onFreqSelected="onFreqSelected"
            @onFreqOption="onFreqOption"
          />
          <template v-slot:footer>
            <back-to-top refName="scroll_content"/>
          </template>
        </component>
        <ai-report-floating-button
          v-if="!isAiDisabled"
          :apiParamsData="apiParamsData"
          :metric="metric"
          :freqPv="freqPv"
          :isFrequency="isFrequency"
          :segmentsDataForCapture="segmentsDataForCapture"
          :noData="noData"
          @onReportLoading="onReportLoading"
        />
      </div>
      <div class="scroll_content-loading">
        <ui-loading-circle v-if="isLoading" :applyBackground="true"/>
      </div>
      <download-error :isDownloading="isDownloading"
                      @onDownloading="onDownloading"></download-error>
    </scroll-content>
    <ui-loading-circle v-if="isReportLoading" :applyBackground="true"/>
  </div>
</template>

<script>
import Heatmap from '@/components/heatmap'
import DownloadError from '@/components/heatmap/HeatmapDownloadError'
import ReportClick from '@/components/reporting-heatmaps/ReportClick'
import ReportTap from '@/components/reporting-heatmaps/ReportTap'
import ReportZoom from '@/components/reporting-heatmaps/ReportZoom'
import ReportMove from '@/components/reporting-heatmaps/ReportMove'
import ReportSwipe from '@/components/reporting-heatmaps/ReportSwipe'
import ReportScroll from '@/components/reporting-heatmaps/ReportScroll'
import ReportStream from '@/components/reporting-heatmaps/ReportStream'
import AiReportFloatingButton from '@/components/reporting-heatmaps/AiReportFloatingButton.vue'
import ScrollContent from '@/components/ui/ScrollContent'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle'
import BackToTop from '@/components/ui/BackToTop'
import { mapState } from 'vuex'
import { isAiDisabledAccount } from '@/libs/utils'
import ReportTimeSeries from '@/components/reporting-heatmaps/ReportTimeSeries.vue'
import report from '@/views/member/report/index.vue'
import ReportFrequency from '@/components/reporting-heatmaps/ReportFrequency.vue'

export default {
  name: 'HeatmapReport',
  components: {
    ReportFrequency,
    ReportTimeSeries,
    Heatmap,
    ReportClick,
    ReportTap,
    ReportZoom,
    ReportMove,
    ReportSwipe,
    ReportScroll,
    ReportStream,
    AiReportFloatingButton,
    ScrollContent,
    UiLoadingCircle,
    BackToTop,
    DownloadError
  },
  props: [
    'cacheManager',
    'isAMP'
  ],
  data () {
    return {
      initialEventType: this.$route.query.eventType,
      reportData: null,
      reportName: null,
      referer: null,
      metric: null,
      activeSelector: null,
      userActionData: null,
      conversion: null,
      activityStatistics: null,
      infoData: null,
      isLoading: true,
      isDownloading: false,
      heatmapType: 0,
      startDate: null,
      endDate: null,
      timeSeries: null,
      hourData: null,
      isFrequency: false,
      freqData: null,
      freqMeanData: null,
      freqPv: [],
      freqTotalPv: null,
      apiParamsData: null,
      freqOption: null,
      isReportLoading: false,
      segmentsDataForCapture: null,
      device: null,
      allReportData: {}
    }
  },
  computed: {
    report () {
      return report
    },
    ...mapState([
      'applyData',
      'reportPeriod',
      'planData',
      'userData'
    ]),
    isAiDisabled () {
      return isAiDisabledAccount(this.userData)
    },
    aid () {
      return this.$route.params.aid
    },
    reportComponent () {
      switch (this.reportName) {
        case 'click':
          return 'ReportClick'
        case 'tap':
          return 'ReportTap'
        case 'zoom':
          return 'ReportZoom'
        case 'move':
          return 'ReportMove'
        case 'swipe':
          return 'ReportSwipe'
        case 'scroll':
          return 'ReportScroll'
        case 'stream':
          return 'ReportStream'
      }

      if (this.isAMP) {
        switch (this.reportName) {
          case 'click':
          case 'tap':
            return 'ReportClick'
          case 'zoom':
          case 'move':
            return 'ReportMove'
          case 'scroll':
            return 'ReportScroll'
          case 'stream':
            return 'ReportStream'
        }
      }
      return null
    },
    isPlusPlan () {
      return this.planData.planNumber > 4000
    },
    noData () {
      if (!this.reportData || !this.reportData.data) return true
      if (!this.infoData || !this.infoData.device) return true

      const deviceData = this.infoData.device[this.device]

      if (!Object.values(this.infoData.device).length || (deviceData && deviceData.pv === 0)) {
        return true
      }

      // 모든 이벤트 타입의 데이터 확인
      const clickData = this.allReportData.click || this.allReportData.tap
      const scrollData = this.allReportData.scroll

      const hasClickTapData = clickData && clickData.data && (
        (clickData.data.topClicks && clickData.data.topClicks.length > 0) ||
        (clickData.data.topPvs && clickData.data.topPvs.length > 0)
      )
      const hasScrollData = scrollData && scrollData.data && scrollData.data.scroll && scrollData.data.scroll.attentionItems

      // 클릭/탭/스크롤 중 하나라도 있으면 활성 (다른 이벤트 데이터 유무 상관없음)
      if (hasClickTapData || hasScrollData) {
        return false
      }

      // 클릭/탭/스크롤이 없으면 비활성
      return true
    }
  },
  watch: {
    aid (value) {
      this.allReportData = {}
      this.reload()
    },
    reportPeriod: {
      handler (newVal, oldVal) {
        if (newVal.start !== oldVal.start || newVal.end !== oldVal.end) {
          this.hourData = null
          this.allReportData = {}
        }
      },
      deep: true
    }
  },
  methods: {
    onSegmentsData (value) {
      this.segmentsDataForCapture = value
    },
    setHeatmapType (value) {
      this.$emit('setHeatmapType', value)
      this.heatmapType = value
    },
    onHeatmapData (key, value) {
      this.reportName = key
      this.reportData = value
      if (value) {
        this.allReportData[key] = value
      }
      if (key !== 'stream') {
        this.activityStatistics = 'median'
        this.conversion = null
      }
    },
    onInfoData (value) {
      this.infoData = value
    },
    reload () {
    },
    onDownloading (value) {
      this.isDownloading = value
    },
    onReportLoading (value) {
      this.isReportLoading = value
    },
    onHeatmapLoadStart (type) {
      this.isLoading = true
    },
    onHeatmapLoadEnd (type) {
      this.isLoading = false
    },
    backToTop () {
      this.$refs.reportComponent.$el.scrollIntoView(true)
    },
    onDeviceDataByHour (value) {
      this.hourData = String(value)
    },
    onTimeSeries (value) {
      this.timeSeries = value
    },
    showFrequency (isShow) {
      this.isFrequency = isShow
      if (!isShow) {
        this.freqTotalPv = null
        this.freqPv = null
      }
    },
    onFreqData (pvData, mean) {
      this.freqData = pvData
      this.freqMeanData = mean
    },
    onFreqSelected (pv) {
      this.freqPv = pv
    },
    getFreqTotalPv (pv) {
      this.freqTotalPv = pv
    },
    apiParams (params) {
      this.apiParamsData = params
    },
    onFreqOption (option) {
      this.freqOption = option
    }
  }
}
</script>

<style lang="scss">
.scroll_content-loading {
  position: fixed;
  top: 50%;
  z-index: 11;
  left: calc(1010px + (100% - 1010px) / 2); // 1010 = nav bar width + fixed content width

  .loading {
    position: static !important;
  }
}

.reporting-heatmap-analysis {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;

  .content-box {
    width: 100%;
    border-radius: 14px;
    background-color: #ffffff;
    padding: 25px;
    box-sizing: border-box;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
  }

  .fan-o-meter {
    color: #444;
    font-size: 22px;
    font-weight: 500;
    line-height: 22px; /* 100% */
    margin-bottom: 20px;
  }

  .no-data {
    position: relative;
    margin-top: 30px;
    height: 295px;
    border-radius: 14px;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-color: #ffffff;

    p {
      font-weight: 500;
      width: 100%;
      font-size: 13px;
      line-height: 1.46;
      color: #2f2f2f;
      text-align: center;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
  .more-info--wrap {
    & > div {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 830px;
      height: 574px;
      border-radius: 14px;
      box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
      background-color: #ffffff;
      color: #666666;
      padding: 30px 30px 35px;
      box-sizing: border-box;
      overflow: hidden;
    }

    table {
      width: 100%;
      text-align: center;
      table-layout: fixed;
      border-collapse: collapse;

      caption {
        text-align: left;
        font-size: 15px;
        font-weight: 600;
        color: #2f2f2f;
        margin-bottom: 50px;

        span {
          font-size: 12px;
          color: $brandColor;
          margin-left: 20px;
          display: inline-block;
        }
      }

      thead tr{
        display: block;
        th {
          padding-bottom: 8px;
          border-bottom: 1px solid #bbbbbb;
          line-height: 1.23;
          font-size: 13px;
          color: #666666;

          span {
            display: inline-block;
            white-space: nowrap;
            width: 63px;
            text-align: right;
            position: relative;
            left: -10px;
          }
        }
      }

      tbody {
        display: block;
        overflow: auto;
        width: 100%;
        height: 420px;
        color: #2f2f2f;

        tr {
          height: 50px;
          border-bottom: 1px solid #ebebeb;
          box-sizing: border-box;
          font-weight: 500;
        }
      }
    }

    button {
      position: absolute;
      top: 0;
      right: 0;
      padding: 12px;
    }
  }
}
.heatmap-report {
  .btn__back-to-top {
    cursor: pointer;
    width: 100px;
    margin: 40px auto 0;
    text-align: center;

    p {
      margin-top: 8px;
    }
  }
}
.ie .reporting-heatmap-analysis {
  margin-bottom: 40px;
}
.popup__error {
  @include positionXYcenter;
  width: 430px;
  border-radius: 14px;
  box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
  background-color: #ffffff;
  padding: 50px 30px 25px;
  box-sizing: border-box;
  overflow: hidden;
  text-align: center;

  .title {
    margin-top: 15px;
    font-size: 20px;
    font-weight: 600;
    text-align: center;
    color: #444444;
  }

  .desc {
    margin-top: 15px;
    font-size: 14px;
    text-align: center;
    color: #2f2f2f;
  }

  .ui-btn {
    margin-top: 60px;
  }
}
</style>
